/**
 * Маппинг backend-кодов ошибок в пользовательские сообщения
 *
 * Основные принципы:
 * 1. Сохранять безопасность — не раскрывать внутреннюю информацию
 * 2. Быть понятным для пользователя
 * 3. Следовать UX-стандартам проекта
 */

export type ErrorCode =
  // Основные ошибки авторизации
  | 'WRONG_PASSWORD'
  | 'USER_NOT_FOUND'
  | 'EMAIL_EXISTS'
  | 'PHONE_EXISTS'
  | 'INVALID_OTP'
  | 'USER_NOT_CONFIRMED'
  | 'MISSING_SESSION'
  | 'INVALID_SESSION'

  // Flash-call ошибки
  | 'FLASHCALL_NETWORK_ERROR'
  | 'FLASHCALL_PROVIDER_ERROR'
  | 'FLASHCALL_INVALID_RESPONSE'

  // SMS ошибки
  | 'SMS_NETWORK_ERROR'
  | 'SMS_PROVIDER_ERROR'
  | 'SMS_INVALID_RESPONSE'

  // Общие ошибки
  | 'VALIDATION_ERROR'
  | 'UNEXPECTED_ERROR'
  | string; // Для неизвестных кодов

/**
 * Основной маппинг код → пользовательское сообщение
 */
const ERROR_MAPPINGS: Record<string, string> = {
  // Основные ошибки авторизации
  WRONG_PASSWORD: 'Неверный пароль',
  USER_NOT_FOUND: 'Пользователь с таким email не найден',
  EMAIL_EXISTS: 'Email уже зарегистрирован',
  PHONE_EXISTS: 'Телефон уже зарегистрирован',
  INVALID_OTP: 'Неверный или просроченный код подтверждения',
  USER_NOT_CONFIRMED: 'Email не подтверждён. Проверьте почту.',
  MISSING_SESSION: 'Требуется авторизация',
  INVALID_SESSION: 'Сессия устарела. Войдите заново.',

  // Flash-call ошибки
  FLASHCALL_NETWORK_ERROR: 'Ошибка подключения к сервису flash-call',
  FLASHCALL_PROVIDER_ERROR: 'Сервис flash-call временно недоступен',
  FLASHCALL_INVALID_RESPONSE: 'Ошибка в ответе сервиса flash-call',

  // SMS ошибки
  SMS_NETWORK_ERROR: 'Ошибка подключения к SMS-сервису',
  SMS_PROVIDER_ERROR: 'SMS-сервис временно недоступен',
  SMS_INVALID_RESPONSE: 'Ошибка в ответе SMS-сервиса',

  // Общие ошибки
  VALIDATION_ERROR: 'Некорректные данные',
  UNEXPECTED_ERROR: 'Произошла непредвиденная ошибка',
};

/**
 * Преобразует backend-код ошибки в пользовательское сообщение
 *
 * @param code Код ошибки от сервера
 * @param defaultMessage Сообщение по умолчанию, если код не найден
 * @returns Пользовательское сообщение
 */
export function mapErrorCode(code: string, defaultMessage?: string): string {
  const mapped = ERROR_MAPPINGS[code];
  if (mapped) {
    return mapped;
  }

  // Для неизвестных кодов используем сообщение по умолчанию или сам код
  return defaultMessage || `Ошибка: ${code}`;
}

/**
 * Определяет, является ли код ошибкой сетевого уровня
 * (требует отдельного fallback-сообщения)
 */
export function isNetworkErrorCode(code: string): boolean {
  return code === 'FLASHCALL_NETWORK_ERROR' ||
         code === 'SMS_NETWORK_ERROR' ||
         code.includes('NETWORK') ||
         code.includes('TIMEOUT');
}

/**
 * Определяет, является ли код ошибкой провайдера (внешнего сервиса)
 */
export function isProviderErrorCode(code: string): boolean {
  return code === 'FLASHCALL_PROVIDER_ERROR' ||
         code === 'SMS_PROVIDER_ERROR' ||
         code.includes('PROVIDER');
}

/**
 * Получает пользовательское сообщение для ошибки API
 *
 * Приоритет:
 * 1. Маппинг по code, если он известен
 * 2. Первое сообщение из errors массива, если есть
 * 3. Fallback сообщение
 */
export function getUserErrorMessage(
  code?: string,
  errors?: string[],
  fallbackMessage: string = 'Произошла ошибка'
): string {
  // Если есть известный код — используем маппинг
  if (code) {
    return mapErrorCode(code, fallbackMessage);
  }

  // Если нет кода, но есть сообщения от сервера — используем первое
  if (errors && errors.length > 0) {
    return errors[0] ?? fallbackMessage;
  }

  // Fallback
  return fallbackMessage;
}

/**
 * Создаёт массив ошибок для UI на основе данных от сервера
 */
export function createUiErrors(
  code?: string,
  serverErrors?: string[]
): string[] {
  const message = getUserErrorMessage(code, serverErrors);
  return [message];
}